import {
  Box,
  Button,
  Checkbox,
  Dialog,
  Divider,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../global/theme/tokens';
import { useState } from 'react';
import FeedbackButton from '../special/FeedbackButton';

/**
 * Generic share dialog for copying contact/lead data to clipboard.
 *
 * @param {boolean} open - Controls dialog visibility
 * @param {function} onClose - Called when dialog should close
 * @param {Array} groups - Array of group objects: { label?: string, items: [{ id, label, secondary?, defaultChecked? }] }
 * @param {function} buildText - Receives map of selected item ids (id -> boolean), returns the string to copy
 */
const ExoShareDialog = ({ open, onClose, groups, buildText }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [fields, setFields] = useState({});
  const [copySuccess, setCopySuccess] = useState(null);

  function initFields() {
    const f = {};
    groups?.forEach((group) => {
      group.items?.forEach((item) => {
        f[item.id] = item.defaultChecked !== false;
      });
    });
    setFields(f);
  }

  function handleOpen() {
    initFields();
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(buildText(fields));
      setCopySuccess(true);
      setTimeout(() => {
        setCopySuccess(null);
        onClose();
      }, 1000);
    } catch {
      setCopySuccess(false);
      setTimeout(() => setCopySuccess(null), 2000);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionProps={{ onEnter: handleOpen }}
      sx={{
        '.MuiPaper-root': {
          backgroundImage: 'unset!important',
          width: '420px',
          maxHeight: '80vh',
          bgcolor: 'transparent',
          boxShadow: 'none',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
        '.MuiBackdrop-root': {
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
        },
      }}
    >
      <Box className="flex flex-col flex-1 min-h-0">
        <Box className="p-4 pb-2 flex-shrink-0">
          <Typography variant="h4" fontWeight={500} className="pb-1">
            {t('Share contact data')}
          </Typography>
        </Box>
        <Box className="flex flex-col gap-3 px-4 pb-2 overflow-y-auto flex-1 min-h-0">
          <Box className="flex flex-col gap-3">
            {groups?.map((group, gi) => {
              const visibleItems = group.items?.filter(
                (item) => item.id in fields,
              );
              if (!visibleItems?.length) return null;

              const groupIds = visibleItems.map((item) => item.id);
              const allGroupChecked = groupIds.every((id) => fields[id]);
              const showGroupToggle = visibleItems.length > 4;

              return (
                <ShareGroup key={gi} colors={colors}>
                  {visibleItems.map((item, i) => (
                    <Box key={item.id}>
                      {i > 0 && (
                        <Divider sx={{ borderColor: colors.selected }} />
                      )}
                      <ShareRow
                        checked={fields[item.id]}
                        onChange={
                          item.locked
                            ? undefined
                            : (v) =>
                                setFields((p) => ({ ...p, [item.id]: v }))
                        }
                        locked={item.locked}
                        label={item.label}
                        secondary={item.secondary}
                        colors={colors}
                      />
                    </Box>
                  ))}
                  {showGroupToggle && (
                    <>
                      <Divider sx={{ borderColor: colors.selected }} />
                      <Box
                        className="flex items-center justify-center cursor-pointer py-1"
                        onClick={() => {
                          const updated = {};
                          groupIds.forEach((id) => {
                            updated[id] = !allGroupChecked;
                          });
                          setFields((p) => ({ ...p, ...updated }));
                        }}
                        sx={{
                          ':hover': { bgcolor: colors.selected },
                        }}
                      >
                        <Typography
                          fontSize="12px"
                          className="opacity-50"
                          sx={{ textTransform: 'none' }}
                        >
                          {allGroupChecked
                            ? t('Deselect all')
                            : t('Select all')}
                        </Typography>
                      </Box>
                    </>
                  )}
                </ShareGroup>
              );
            })}
          </Box>
        </Box>

        <Box className="flex flex-col gap-2 p-4 pt-2 flex-shrink-0">
          <Button
            onClick={() => {
              const allChecked = Object.values(fields).every(Boolean);
              const updated = {};
              Object.keys(fields).forEach((k) => {
                updated[k] = !allChecked;
              });
              setFields(updated);
            }}
            sx={{
              color: colors.text,
              textTransform: 'none',
              fontSize: '13px',
            }}
          >
            {Object.values(fields).every(Boolean)
              ? t('Deselect all')
              : t('Select all')}
          </Button>

          <FeedbackButton
            onClick={handleCopy}
            success={copySuccess}
            variant="contained"
            className="w-full"
          >
            {copySuccess === null && t('Copy')}
          </FeedbackButton>
        </Box>
      </Box>
    </Dialog>
  );
};

const ShareGroup = ({ children, colors }) => {
  return (
    <Box
      className="rounded-xl overflow-hidden backdrop-blur-md"
      sx={{
        bgcolor: colors.card,
      }}
    >
      {children}
    </Box>
  );
};

const ShareRow = ({ checked, onChange, label, secondary, colors, locked }) => {
  return (
    <Box
      className={`flex items-center gap-3 px-3 py-2${locked ? '' : ' cursor-pointer'}`}
      onClick={locked ? undefined : () => onChange(!checked)}
      sx={{
        ':hover': locked
          ? {}
          : {
              bgcolor: colors.selected,
            },
      }}
    >
      {!locked && (
        <Checkbox
          checked={Boolean(checked)}
          size="small"
          sx={{
            p: 0,
            color: colors.info + '80',
            '&.Mui-checked': {
              color: colors.info,
            },
          }}
        />
      )}
      <Box className="flex flex-col">
        {secondary && (
          <Typography fontSize="11px" className="opacity-50">
            {secondary}
          </Typography>
        )}
        <Typography fontSize="15px" fontWeight={500}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
};

export default ExoShareDialog;
